import PropTypes from "prop-types";
import { Fragment, useEffect } from "react";
import { useDispatch } from "react-redux";

// mui components
import { useTheme, styled } from "@mui/material/styles";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import { Checkbox, FormControlLabel, Typography } from "@mui/material";

// mui icons
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";

// redux
import { loadCheckedVariablesState } from "store/features/matchNetSlice";

const Accordion = styled((props) => (
    <MuiAccordion
        disableGutters
        elevation={0}
        square
        {...props}
    />
))(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    "&:not(:last-child)": { borderBottom: 0 },
    "&::before": { display: "none" },
}));

const AccordionSummary = styled((props) => (
    <MuiAccordionSummary
        expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
        {...props}
    />
))(({ theme }) => ({
    backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, .05)" : "rgba(0, 0, 0, .03)",
    flexDirection: "row-reverse",
    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": { transform: "rotate(90deg)" },
    "& .MuiAccordionSummary-content": { marginLeft: theme.spacing(1) },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
    padding: theme.spacing(2),
    borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

function getPerformanceColumns(mapping) {
    const allCategories = mapping?.all_mapping?.columns ?? [];
    const defaultCategories = mapping?.default_mapping?.columns ?? [];

    const performanceColumns = [];

    for (const category of allCategories) {
        // If this category already exists in the default list, skip it entirely
        const isAlreadyDefault = defaultCategories.some((defaultCat) => defaultCat.macroCategory === category.macroCategory);

        if (!isAlreadyDefault) {
            performanceColumns.push(category);
        }
    }

    return performanceColumns;
}

function getOrderedMapping(mapping, removed) {
    const columns = [...(mapping?.default_mapping?.columns ?? []), ...getPerformanceColumns(mapping)];
    return columns.map((cat) => ({ ...cat, displayLabels: cat.displayLabels.filter((l) => !removed.includes(l.backendVariable)) })).filter((cat) => cat.displayLabels.length > 0);
}

export default function MatchNetSideBar({ studyMethodsList = [], setVariableMapping = () => {}, setRemovedKpi = () => {}, selectedVariables = {}, setSelectedVariables = () => {} }) {
    const theme = useTheme();
    const dispatch = useDispatch();

    // const checkedVariablesList = useSelector((state) => state.matchNetStore.checkedVariables);

    const checkboxSx = { color: theme.palette.text.primary, background: "none", borderRadius: "10px", width: "100%", padding: "0px" };
    const sectionTypographySx = { className: "text-xs", style: { fontSize: "0.8rem" }, sx: { mt: 2, mb: 1, px: "4px", fontWeight: 700 } };
    const sectionTypographyMainSx = { className: "text-xs", style: { fontSize: "0.95rem" }, sx: { mt: 2, mb: 0, px: "4px", fontWeight: 600 } };
    // Automatically keep Redux checkedVariables in sync with selectedVariables
    useEffect(() => {
        if (!studyMethodsList?.length) return;
        const checkedMap = {};
        studyMethodsList.forEach((datum) => {
            const title = datum.study_type_method_title;
            const removed = selectedVariables[title] || [];
            const defaultBvs = (datum.mapping?.default_mapping?.columns ?? []).flatMap((cat) => cat.displayLabels.map((l) => l.backendVariable));
            const performanceBvs = getPerformanceColumns(datum.mapping).flatMap((cat) => cat.displayLabels.map((l) => l.backendVariable));
            const allBvs = [...defaultBvs, ...performanceBvs];
            checkedMap[title] = allBvs.filter((bv) => !removed.includes(bv));
        });
        dispatch(loadCheckedVariablesState(checkedMap));
    }, [selectedVariables, studyMethodsList, dispatch]);

    const handleCheckboxChange = (event, category, label, index, studyTypeMethodTitle) => {
        const backendVariable = label.backendVariable;

        if (event.target.checked) {
            setRemovedKpi((prevState) => prevState.filter((item) => item !== backendVariable));
            setSelectedVariables((prevState) => ({
                ...prevState,
                [studyTypeMethodTitle]: prevState[studyTypeMethodTitle].filter((item) => item !== backendVariable),
            }));
            setVariableMapping((prev) =>
                prev.map((item, i) => {
                    if (i !== index) return item;
                    const newRemoved = selectedVariables[studyTypeMethodTitle].filter((item) => item !== backendVariable);
                    return {
                        ...item,
                        mapping: getOrderedMapping(studyMethodsList[index].mapping, newRemoved),
                    };
                })
            );
        } else {
            setRemovedKpi((prevState) => [...prevState, backendVariable]);
            setSelectedVariables((prevState) => ({
                ...prevState,
                [studyTypeMethodTitle]: [...prevState[studyTypeMethodTitle], backendVariable],
            }));
            setVariableMapping((prev) =>
                prev.map((item, i) => {
                    if (i !== index) return item;
                    const newRemoved = [...selectedVariables[studyTypeMethodTitle], backendVariable];
                    return {
                        ...item,
                        mapping: getOrderedMapping(studyMethodsList[index].mapping, newRemoved),
                    };
                })
            );
        }
    };

    const handleSelectAllChange = (event, category, index, studyTypeMethodTitle) => {
        const backendVariables = category.displayLabels.map((item) => item.backendVariable);

        if (event.target.checked) {
            setRemovedKpi((prevState) => prevState.filter((item) => !backendVariables.includes(item)));
            setSelectedVariables((prevState) => ({
                ...prevState,
                [studyTypeMethodTitle]: prevState[studyTypeMethodTitle].filter((item) => !backendVariables.includes(item)),
            }));
            setVariableMapping((prev) =>
                prev.map((item, i) => {
                    if (i !== index) return item;
                    const newRemoved = selectedVariables[studyTypeMethodTitle].filter((item) => !backendVariables.includes(item));
                    return {
                        ...item,
                        mapping: getOrderedMapping(studyMethodsList[index].mapping, newRemoved),
                    };
                })
            );
        } else {
            setRemovedKpi((prevState) => [...new Set([...prevState, ...backendVariables])]);
            setSelectedVariables((prevState) => ({
                ...prevState,
                [studyTypeMethodTitle]: [...new Set([...prevState[studyTypeMethodTitle], ...backendVariables])],
            }));
            setVariableMapping((prev) =>
                prev.map((item, i) => {
                    if (i !== index) return item;
                    const newRemoved = [...new Set([...selectedVariables[studyTypeMethodTitle], ...backendVariables])];
                    return {
                        ...item,
                        mapping: getOrderedMapping(studyMethodsList[index].mapping, newRemoved),
                    };
                })
            );
        }
    };

    const renderCategory = (category, index, studyTypeMethodTitle) => {
        const id = category.macroCategory.toLowerCase().replace(/\s+/g, "-");
        const isAllChecked = Object.keys(selectedVariables).includes(studyTypeMethodTitle) ? category.displayLabels.every((item) => !selectedVariables[studyTypeMethodTitle].includes(item.backendVariable)) : false;
        return (
            <Accordion
                key={category.macroCategory}
                sx={{ background: "#dbdbdb2b" }}
            >
                <AccordionSummary
                    aria-controls={`${id}-content`}
                    id={`${id}-header`}
                >
                    <Typography
                        className="text-xs"
                        style={{ fontSize: "0.75rem" }}
                        sx={{ margin: 0, paddingLeft: "4px" }}
                    >
                        {category.macroCategory}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ padding: "0px 10px" }}>
                    {/* Select All row */}
                    <FormControlLabel
                        sx={{ ...checkboxSx, margin: "3px -10px" }}
                        control={
                            <Checkbox
                                size="small"
                                checked={isAllChecked}
                                onChange={(e) => handleSelectAllChange(e, category, index, studyTypeMethodTitle)}
                            />
                        }
                        label={<span style={{ fontSize: "0.75rem", marginLeft: "5px" }}>Select All</span>}
                        aria-label={`Select all in ${category.macroCategory}`}
                    />
                    {/* Individual variable rows */}
                    {category.displayLabels?.map((label) => (
                        <FormControlLabel
                            key={label.backendVariable}
                            className="dashboard-filter-label"
                            sx={{ ...checkboxSx, margin: "3px 0px" }}
                            control={
                                <Checkbox
                                    size="small"
                                    color="primary"
                                    onChange={(e) => handleCheckboxChange(e, category, label, index, studyTypeMethodTitle)}
                                    checked={Object.keys(selectedVariables).includes(studyTypeMethodTitle) ? !selectedVariables[studyTypeMethodTitle].includes(label.backendVariable) : false}
                                />
                            }
                            label={<span style={{ fontSize: "0.75rem" }}>{label.displayLabel}</span>}
                        />
                    ))}
                </AccordionDetails>
            </Accordion>
        );
    };

    const renderSection = (columns, studyTypeMethodTitle, index) => {
        if (!columns?.length) return null;
        return (
            <>
                <Typography {...sectionTypographySx}>{studyTypeMethodTitle}</Typography>
                {columns.map((category) => renderCategory(category, index, studyTypeMethodTitle))}
            </>
        );
    };

    return (
        <div style={{ fontSize: "14px", color: "#000", overflowY: "auto", marginBottom: "10px" }}>
            <Typography {...sectionTypographyMainSx}>Default Variables</Typography>
            <hr style={{ margin: 0 }} />
            {studyMethodsList.map((studyMethod, index) => (
                <Fragment key={studyMethod.study_type_method || studyMethod.study_type_method_title}>{renderSection(studyMethod.mapping?.default_mapping?.columns, studyMethod.study_type_method_title, index)}</Fragment>
            ))}
            <Typography {...sectionTypographyMainSx}>Performance Variables</Typography>
            <hr style={{ margin: 0 }} />
            {studyMethodsList.map((studyMethod, index) => (
                <Fragment key={studyMethod.study_type_method || studyMethod.study_type_method_title}>{renderSection(getPerformanceColumns(studyMethod.mapping), studyMethod.study_type_method_title, index)}</Fragment>
            ))}
        </div>
    );
}

MatchNetSideBar.propTypes = {
    studyMethodsList: PropTypes.array,
    setVariableMapping: PropTypes.func,
    setRemovedKpi: PropTypes.func,
    selectedVariables: PropTypes.object,
    setSelectedVariables: PropTypes.func,
};
